import { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import * as Icons from 'lucide-react';
import { api } from '../lib/api-client';
import Breadcrumbs from '../components/navigation/Breadcrumbs';
import BackButton from '../components/navigation/BackButton';

interface SectionPageProps {
  sectionId: string;
  goHome: () => void;
  goToSection: (id: string) => void;
  goToTopic: (sectionId: string, topicId: string) => void;
  isHI?: boolean;
  goHIHome?: () => void;
}

function getIcon(name: string) {
  return (Icons as any)[name] || Icons.FileText;
}

export default function SectionPage({
  sectionId,
  goHome,
  goToTopic,
  isHI,
  goHIHome,
}: SectionPageProps) {
  const [section, setSection] = useState<any>(null);
  const [articles, setArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api.getSection(sectionId).then((data) => {
      if (!cancelled) {
        setSection(data.section);
        setArticles(data.articles || []);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [sectionId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-content-border border-t-oak-green rounded-full animate-spin" />
      </div>
    );
  }

  if (!section) {
    return <div className="text-content-secondary text-center py-20">Section not found</div>;
  }

  const Icon = getIcon(section.icon);

  // Build breadcrumbs
  const breadcrumbs = [];
  if (isHI) {
    breadcrumbs.push({ label: '🌳 OAK Library', onClick: goHome });
    breadcrumbs.push({ label: '🏥 Health Insurance', onClick: goHIHome || (() => {}) });
    breadcrumbs.push({ label: section.title, onClick: () => {} });
  } else {
    breadcrumbs.push({ label: '🌳 OAK Library', onClick: goHome });
    breadcrumbs.push({ label: section.title, onClick: () => {} });
  }

  // Back button
  const backLabel = isHI ? '← Back to Health Insurance Home' : '← Back to Home';
  const backAction = isHI ? (goHIHome || goHome) : goHome;

  return (
    <div className="max-w-[800px]">
      <Breadcrumbs items={breadcrumbs} />

      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-[14px] bg-oak-green/[0.15] flex items-center justify-center">
          <Icon size={28} className="text-oak-green" />
        </div>
        <div>
          <h1 className="text-[28px] font-extrabold text-navy mb-1">{section.title}</h1>
          {section.description && (
            <p className="text-[15px] text-content-secondary">{section.description}</p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        {articles.map((topic: any) => (
          <TopicRow
            key={topic.id}
            topic={topic}
            onClick={() => goToTopic(sectionId, topic.id)}
          />
        ))}
        {articles.length === 0 && (
          <div className="text-center py-12 text-content-secondary">
            No published topics yet in this section.
          </div>
        )}
      </div>

      <BackButton label={backLabel} onClick={backAction} />
    </div>
  );
}

function TopicRow({ topic, onClick }: { topic: any; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`flex items-center justify-between bg-white rounded-[10px] border py-[18px] px-5 cursor-pointer transition-all duration-150 text-left ${
        hovered ? 'border-oak-green translate-x-1' : 'border-content-border'
      }`}
    >
      <div>
        <span className="text-oak-green text-sm font-semibold mr-3">{topic.id}</span>
        <span className="font-medium text-content-primary text-[15px]">{topic.title}</span>
      </div>
      <ChevronRight size={18} className="text-content-secondary" />
    </button>
  );
}
